import * as R from 'ramda'
import React from 'react'

import { SurveyConfigurationContext } from 'organisms/SurveyConfiguration/index'
import CallToActionButton from 'components/CallToActionButton'

const iconImages = {
  googlePlayStore: 'https://cdn.simplesat.io/images/cta-buttons/googleplay-icon.png',
  trustpilot: 'https://cdn.simplesat.io/images/cta-buttons/trustpilot-icon.png',
  g2Crowd: 'https://cdn.simplesat.io/images/cta-buttons/g2crowd-icon.png',
  capterra: 'https://cdn.simplesat.io/images/cta-buttons/capterra-icon.ico',
  amazon: 'https://cdn.simplesat.io/images/cta-buttons/amazon-white-icon.png',
  hostAdvice: 'https://cdn.simplesat.io/images/cta-buttons/hostadvice-icon.png',
  websitePlanet: 'https://cdn.simplesat.io/images/cta-buttons/websiteplanet-icon.png',
  whTop: 'https://cdn.simplesat.io/images/cta-buttons/whtop-icon.png',
}

/**
 * @param {object} props
 * @param {string} props.ratingType
 * @param {object} props.callToActionButtons
 * @param {function} props.onCallToActionSettingsChange
 * @param {object} props.surveyLanguage
 */
function CallToActionButtonsSettings({
  ratingType,
  callToActionButtons,
  onCallToActionSettingsChange,
  surveyLanguage,
}) {
  const createOnTextChange =
    ({ type, buttonLabel }) =>
    (event) => {
      const { name, value } = event.target
      const merge = R.compose(
        R.mergeDeepRight({ [type]: { isActive: false } }),
        R.mergeDeepRight(callToActionButtons[ratingType])
      )
      const state = merge({
        [type]: { label: buttonLabel, [name]: value },
      })

      onCallToActionSettingsChange(ratingType, state)
    }

  const createOnToggle =
    ({ type, buttonLabel }) =>
    (_, { name }) => {
      const prevChecked = R.path([ratingType, type, 'isActive'], callToActionButtons)
      const state = R.mergeDeepRight(callToActionButtons[ratingType], {
        [type]: { label: buttonLabel, [name]: !prevChecked },
      })

      onCallToActionSettingsChange(ratingType, state)
    }

  const {
    google,
    facebook,
    custom,
    yelp,
    trustpilot,
    g2Crowd,
    capterra,
    googlePlayStore,
    amazon,
    appleAppStore,
    hostAdvice,
    websitePlanet,
    whTop,
  } = callToActionButtons[ratingType]

  return (
    <React.Fragment>
      <h3>Call to action buttons</h3>
      <p>
        <a
          href="https://help.simplesat.io/en/articles/7171943-survey-settings#call-to-action-buttons"
          target="_blank"
          rel="noreferrer"
        >
          <i className="icon far fa-book" />
          Managing call to action buttons
        </a>
      </p>
      <div className="ui stackable grid">
        <CallToActionButton
          createOnChange={createOnTextChange}
          createOnToggle={createOnToggle}
          type="custom"
          label="Custom"
          buttonLabel={R.prop('label', custom)}
          link={R.prop('link', custom)}
          isActive={R.prop('isActive', custom)}
        />
        {ratingType === 'positive' && (
          <React.Fragment>
            <CallToActionButton
              createOnChange={createOnTextChange}
              createOnToggle={createOnToggle}
              type="google"
              label="Google"
              link={R.prop('link', google)}
              isActive={R.prop('isActive', google)}
              buttonLabel={R.prop('cta_google', surveyLanguage) || 'Review us on Google'}
              iconClass="google g icon"
              buttonStyles={{
                backgroundColor: '#2b84e6',
                color: '#fff',
              }}
            />
            <CallToActionButton
              createOnChange={createOnTextChange}
              createOnToggle={createOnToggle}
              type="facebook"
              label="Facebook"
              link={R.path(['link'], facebook)}
              isActive={R.path(['isActive'], facebook)}
              buttonLabel={R.prop('cta_facebook', surveyLanguage) || 'Review us on Facebook'}
              iconClass="facebook icon"
              buttonClass="facebook"
            />
            <CallToActionButton
              createOnChange={createOnTextChange}
              createOnToggle={createOnToggle}
              type="g2Crowd"
              label="G2 Crowd"
              link={R.prop('link', g2Crowd)}
              isActive={R.prop('isActive', g2Crowd)}
              buttonLabel={R.prop('cta_g_2_crowd', surveyLanguage) || 'Review us on G2 Crowd'}
              iconImage={iconImages['g2Crowd']}
              buttonStyles={{
                backgroundColor: '#ff492c',
                color: '#FFFFFF',
              }}
            />
            <CallToActionButton
              createOnChange={createOnTextChange}
              createOnToggle={createOnToggle}
              type="capterra"
              label="Capterra"
              link={R.prop('link', capterra)}
              isActive={R.prop('isActive', capterra)}
              buttonLabel={R.prop('cta_capterra', surveyLanguage) || 'Review us on Capterra'}
              iconImage={iconImages['capterra']}
              buttonStyles={{
                backgroundColor: '#002E47',
                color: '#FFFFFF',
              }}
            />
            <CallToActionButton
              createOnChange={createOnTextChange}
              createOnToggle={createOnToggle}
              type="yelp"
              label="Yelp"
              link={R.prop('link', yelp)}
              isActive={R.prop('isActive', yelp)}
              buttonLabel={R.prop('cta_yelp', surveyLanguage) || 'Review us on Yelp'}
              iconClass="fab fa-yelp"
              buttonStyles={{
                backgroundColor: '#D42322',
                color: 'white',
              }}
            />
            <CallToActionButton
              createOnChange={createOnTextChange}
              createOnToggle={createOnToggle}
              type="trustpilot"
              label="Trustpilot"
              link={R.prop('link', trustpilot)}
              isActive={R.prop('isActive', trustpilot)}
              buttonLabel={R.prop('cta_trustpilot', surveyLanguage) || 'Review us on Trustpilot'}
              iconImage={iconImages['trustpilot']}
              buttonStyles={{
                backgroundColor: '#010032',
                color: '#FFFFFF',
              }}
            />
            <CallToActionButton
              createOnChange={createOnTextChange}
              createOnToggle={createOnToggle}
              type="amazon"
              label="Amazon"
              iconImage={iconImages['amazon']}
              link={R.prop('link', amazon)}
              isActive={R.prop('isActive', amazon)}
              buttonLabel={R.prop('cta_amazon', surveyLanguage) || 'Review us on Amazon'}
              buttonStyles={{
                backgroundColor: '#222F3E',
                color: '#FFFFFF',
              }}
            />
            <CallToActionButton
              createOnChange={createOnTextChange}
              createOnToggle={createOnToggle}
              type="googlePlayStore"
              label="Google Play Store"
              link={R.prop('link', googlePlayStore)}
              isActive={R.prop('isActive', googlePlayStore)}
              buttonLabel={
                R.prop('cta_google_play_store', surveyLanguage) || 'Review us on Google Play'
              }
              buttonClass="secondary"
              buttonStyles={{
                backgroundColor: '#000000',
                color: 'white',
              }}
              iconImage={iconImages['googlePlayStore']}
            />
            <CallToActionButton
              createOnChange={createOnTextChange}
              createOnToggle={createOnToggle}
              type="appleAppStore"
              label="App Store"
              link={R.prop('link', appleAppStore)}
              isActive={R.prop('isActive', appleAppStore)}
              buttonLabel={
                R.prop('cta_apple_app_store', surveyLanguage) || 'Review us on the App Store'
              }
              iconClass="fab fa-apple"
              buttonClass="secondary"
              buttonStyles={{
                backgroundColor: '#000000',
                color: 'white',
              }}
            />
            <CallToActionButton
              createOnChange={createOnTextChange}
              createOnToggle={createOnToggle}
              type="hostAdvice"
              label="HostAdvice"
              link={R.prop('link', hostAdvice)}
              isActive={R.prop('isActive', hostAdvice)}
              buttonLabel={R.prop('cta_host_advice', surveyLanguage) || 'Review us on HostAdvice'}
              iconImage={iconImages['hostAdvice']}
              buttonStyles={{
                backgroundColor: '#1A1A1A',
                color: '#FFFFFF',
              }}
            />
            <CallToActionButton
              createOnChange={createOnTextChange}
              createOnToggle={createOnToggle}
              type="websitePlanet"
              label="Website Planet"
              link={R.prop('link', websitePlanet)}
              isActive={R.prop('isActive', websitePlanet)}
              buttonLabel={
                R.prop('cta_website_planet', surveyLanguage) || 'Review us on Website Planet'
              }
              iconImage={iconImages['websitePlanet']}
              buttonStyles={{
                backgroundColor: '#231F32',
                color: '#FFFFFF',
              }}
            />
            <CallToActionButton
              createOnChange={createOnTextChange}
              createOnToggle={createOnToggle}
              type="whTop"
              label="WHTop"
              link={R.prop('link', whTop)}
              isActive={R.prop('isActive', whTop)}
              buttonLabel={R.prop('cta_wh_top', surveyLanguage) || 'Review us on WHTop'}
              iconImage={iconImages['whTop']}
              buttonStyles={{
                backgroundColor: '#196FA9',
                color: '#FFFFFF',
              }}
            />
          </React.Fragment>
        )}
      </div>
    </React.Fragment>
  )
}

const withContext = (Component) => {
  const ContextSelector = (props) => (
    <SurveyConfigurationContext.Consumer>
      {(state) => <Component {...state} {...props} />}
    </SurveyConfigurationContext.Consumer>
  )
  return ContextSelector
}

export default withContext(CallToActionButtonsSettings)



// WEBPACK FOOTER //
// ./node_modules/eslint-loader??ref--0-0!./src/organisms/CallToActionButtonsSettings.js